import React, { useRef, useState, useEffect } from "react";
import ReactDOM from "react-dom";
import {
  Wrapper,
  FilterBtn,
  Popover,
  PopoverHeader,
  PopoverBody,
  TagItem,
  Footer,
  ApplyButton,
} from "./styles";

type Props = {
  tag: string[];
  value: Record<string, boolean>;
  onApply: (v: Record<string, boolean>) => void;
};

const Filter = ({ tag, value, onApply }: Props) => {
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const popRef = useRef<HTMLDivElement | null>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [local, setLocal] = useState<Record<string, boolean>>({});
  const [pos, setPos] = useState({ top: 0, left: 0 });

  /* ✅ 부모 상태 동기화 (source of truth) */
  useEffect(() => {
    setLocal(value);
  }, [value]);

  /* ---------- Position ---------- */
  const updatePosition = () => {
    const btn = btnRef.current;
    const pop = popRef.current;
    if (!btn) return;

    const rect = btn.getBoundingClientRect();
    const popWidth = pop?.offsetWidth ?? 240;

    const GAP = 8;
    const VIEWPORT_PADDING = 8;

    let left = rect.left + rect.width / 2;
    const minLeft = popWidth / 2 + VIEWPORT_PADDING;
    const maxLeft = window.innerWidth - popWidth / 2 - VIEWPORT_PADDING;

    left = Math.max(minLeft, Math.min(left, maxLeft));

    setPos({
      top: rect.bottom + GAP,
      left,
    });
  };

  const openPopover = () => {
    updatePosition();
    setIsOpen(true);
  };

  const closePopover = () => setIsOpen(false);

  /* ---------- Toggle ---------- */
  const handleToggle = (key: string) => {
    setLocal((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      onApply(next); // 🔥 즉시 부모 반영
      return next;
    });
  };

  /* ---------- Outside / ESC ---------- */
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        popRef.current &&
        !popRef.current.contains(target) &&
        btnRef.current &&
        !btnRef.current.contains(target)
      ) {
        closePopover();
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") closePopover();
    };

    window.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      window.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [isOpen]);

  const activeCount = Object.values(local).filter(Boolean).length;

  return (
    <Wrapper>
      <FilterBtn ref={btnRef} onClick={openPopover}>
        Filter
      </FilterBtn>

      {isOpen &&
        ReactDOM.createPortal(
          <Popover
            ref={popRef}
            style={{
              top: `${pos.top}px`,
              left: `${pos.left}px`,
            }}
          >
            <PopoverHeader>
              <span>Filter</span>
              {activeCount > 0 && (
                <span className="summary">{activeCount} selected</span>
              )}
            </PopoverHeader>

            <PopoverBody>
              {tag.length === 0 && (
                <div className="empty">No tags available</div>
              )}

              {tag.map((t) => (
                <TagItem
                  key={t}
                  active={!!local[t]}
                  onClick={() => handleToggle(t)}
                >
                  {t}
                </TagItem>
              ))}
            </PopoverBody>

            {/* ✅ Apply 버튼에서만 부모 반영 */}
            <Footer>
              <ApplyButton
                onClick={() => {
                  onApply(local);
                  closePopover();
                }}
              >
                Apply
              </ApplyButton>
            </Footer>
          </Popover>,
          document.body
        )}
    </Wrapper>
  );
};

export default Filter;
